import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Image as ImageIcon, Trash2, Loader2, Search,
  Filter, X, HardDrive, Sparkles, Archive, RotateCcw, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import DocumentPreview from "../components/DocumentPreview";
import { formatBytes } from "../lib/format";

const CATEGORIES = {
  fattura: "Fattura", ricevuta: "Ricevuta", contratto: "Contratto",
  referto_amministrativo: "Referto amm.", id_documento: "Documento ID",
  scadenza_fiscale: "Scadenza fiscale", estratto_conto: "Estratto conto",
  preventivo: "Preventivo", polizza: "Polizza", certificato: "Certificato",
  nota_spese: "Nota spese", altro: "Altro",
};

const CAT_COLORS = {
  fattura: { bg: "#E9F3FF", fg: "#1E5F9E" },
  ricevuta: { bg: "#F0FBF4", fg: "#1F7A2C" },
  contratto: { bg: "#FDF4E9", fg: "#8A5A00" },
  estratto_conto: { bg: "#F7F3FF", fg: "#5B21B6" },
  scadenza_fiscale: { bg: "#FFE9E9", fg: "#B42318" },
  altro: { bg: "#F0F0F0", fg: "#4B5563" },
};

const eur = (n, cur = "EUR") =>
  n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: cur }).format(Number(n));

export default function Documents() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const fileInput = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (category) filters.category = category;
      if (q.trim()) filters.q = q.trim();
      const [list, st] = await Promise.all([api.docsList(filters), api.docsStats()]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setStats(st);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documenti");
    } finally { setLoading(false); }
  }, [category, q]);

  useEffect(() => { load(); }, [load]);

  // debounce q → URL sync
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q.trim()) next.set("q", q.trim()); else next.delete("q");
      if (category) next.set("category", category); else next.delete("category");
      setParams(next, { replace: true });
    }, 300);
    // eslint-disable-next-line
  }, [q, category]);

  const onFilesSelected = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let uploadedCount = 0;
    let dupCount = 0;
    const errors = [];
    for (const f of files) {
      try {
        const res = await api.docsUpload([f], { category: category || undefined });
        uploadedCount += (res.uploaded || []).length;
        dupCount += (res.duplicates || []).length;
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "errore";
        errors.push(`${f.name}: ${msg}`);
      }
      setUploadProgress((p) => ({ ...p, current: p.current + 1 }));
    }
    setUploading(false);
    setUploadProgress(null);
    if (uploadedCount) toast.success(`${uploadedCount} document${uploadedCount === 1 ? "o caricato" : "i caricati"}. Analisi IA in corso…`);
    if (dupCount) toast.message(`${dupCount} duplicat${dupCount === 1 ? "o" : "i"} ignorat${dupCount === 1 ? "o" : "i"}`);
    errors.forEach((e) => toast.error(e));
    load();
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    onFilesSelected(e.dataTransfer.files);
  };

  const del = async (id) => {
    if (!window.confirm("Spostare nel cestino? Potrai ripristinarlo entro 30 giorni.")) return;
    try { await api.docsDelete(id); toast.success("Spostato nel cestino"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const iconForMime = (mime) => {
    if (!mime) return FileText;
    if (mime.startsWith("image/")) return ImageIcon;
    return FileText;
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Centro Documenti</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">
            {category ? CATEGORIES[category] || "Documenti" : "I tuoi documenti"}
          </h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Archivio privato: fatture, ricevute, contratti, referti. OCR + IA analizzano il contenuto e ti aiutano a categorizzare. Nulla è pubblico.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {stats && (
            <Tip label={`${stats.total} documenti · ${formatBytes(stats.total_size_bytes)} totali · ${stats.ai_processed_count} analizzati IA`}>
              <div className="pxos-card px-3 py-1.5 text-xs flex items-center gap-2" data-testid="docs-stats">
                <HardDrive size={12}/> {stats.total} doc · {formatBytes(stats.total_size_bytes)}
              </div>
            </Tip>
          )}
          <Link to="/documenti/cestino" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-black/5" data-testid="docs-goto-trash">
            <Archive size={14}/> Cestino
          </Link>
        </div>
      </div>

      {/* Upload drop-zone */}
      <div
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`pxos-card p-6 text-center border-2 border-dashed transition ${dragActive ? "bg-blue-50" : ""}`}
        style={{ borderColor: dragActive ? "var(--pxos-accent)" : "var(--pxos-border)" }}
        data-testid="docs-dropzone"
      >
        <UploadCloud size={30} className="mx-auto opacity-60"/>
        <div className="mt-2 font-display text-lg font-semibold">
          {uploading ? `Caricamento… (${uploadProgress?.current}/${uploadProgress?.total})` : "Trascina i file qui o clicca per caricare"}
        </div>
        <div className="text-xs opacity-60 mt-1">PDF, JPG, PNG, HEIC, WEBP, DOCX, XLSX, TXT, CSV · Max 50MB/file</div>
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => onFilesSelected(e.target.files)}
                accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.docx,.xlsx,.txt,.csv"
                data-testid="docs-file-input"/>
        <button onClick={() => fileInput.current?.click()} disabled={uploading}
                className="mt-3 pxos-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="docs-upload-btn">
          {uploading ? <Loader2 size={14} className="animate-spin"/> : <UploadCloud size={14}/>}
          Seleziona file
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="docs-filters">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca titolo, fornitore, contenuto…"
                  className="pxos-input pl-8 w-full" data-testid="docs-search"/>
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2" aria-label="Cancella"><X size={14}/></button>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Tip label="Mostra tutte le categorie">
            <button onClick={() => setCategory("")}
                    className={`rounded-full px-3 py-1.5 text-xs border ${!category ? "bg-black text-white" : ""}`}
                    style={!category ? {} : { color: "var(--pxos-text-dark)" }}
                    data-testid="docs-filter-all">Tutte</button>
          </Tip>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <Tip key={k} label={`Filtra per ${v}`}>
              <button onClick={() => setCategory(k)}
                      className={`rounded-full px-3 py-1.5 text-xs border ${category === k ? "font-semibold" : ""}`}
                      style={{ background: category === k ? (CAT_COLORS[k]?.bg || "#E5E7EB") : "transparent",
                               color: CAT_COLORS[k]?.fg || "var(--pxos-text-dark)" }}
                      data-testid={`docs-filter-${k}`}>{v}</button>
            </Tip>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 opacity-70" data-testid="docs-loading"><Loader2 size={16} className="animate-spin"/> Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="pxos-card p-8 text-center opacity-70" data-testid="docs-empty">
          <FileText size={30} className="mx-auto opacity-40"/>
          <div className="mt-2">Nessun documento {category ? `nella categoria "${CATEGORIES[category]}"` : ""} trovato.</div>
          {!q && !category && <div className="text-xs mt-1">Carica il primo documento trascinandolo qui sopra.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="docs-grid">
          {items.map((d) => {
            const Icon = iconForMime(d.mime_type);
            const c = CAT_COLORS[d.category] || CAT_COLORS.altro;
            return (
              <div key={d.id} className="pxos-card p-3 flex flex-col gap-2 hover:shadow-md transition" data-testid="doc-card">
                <Link to={`/documenti/${d.id}`} className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-lg grid place-items-center shrink-0" style={{ background: c.bg }}>
                    <Icon size={18} style={{ color: c.fg }}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--pxos-text-dark)" }} title={d.title || d.filename}>
                      {d.title || d.filename}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5" style={{ color: c.fg }}>
                      {CATEGORIES[d.category] || d.category}
                    </div>
                  </div>
                </Link>
                <div className="text-xs opacity-80 space-y-0.5">
                  {d.provider_name && <div className="truncate"><b>Fornitore:</b> {d.provider_name}</div>}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {d.document_date && <span>{new Date(d.document_date).toLocaleDateString("it-IT")}</span>}
                    {d.amount != null && <span className="font-medium">{eur(d.amount, d.currency)}</span>}
                  </div>
                  {d.ai_confidence != null && (
                    <div className="flex items-center gap-1 text-[10px] opacity-70">
                      <Sparkles size={10}/> IA {(d.ai_confidence * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 pt-1 border-t" style={{ borderColor: "var(--pxos-border)" }}>
                  <button
                    onClick={() => setPreviewId(d.id)}
                    className="text-[10px] font-semibold uppercase tracking-widest opacity-70 hover:opacity-100 inline-flex items-center gap-1 py-1 pr-2"
                    data-testid="doc-preview-btn"
                    title="Apri anteprima veloce"
                  >
                    <Eye size={11}/> Anteprima
                  </button>
                  <Link to={`/documenti/${d.id}`} className="text-[10px] font-semibold uppercase tracking-widest opacity-70 hover:opacity-100 flex-1 py-1" data-testid="doc-open">
                    Apri →
                  </Link>
                  <Tip label="Sposta nel cestino">
                    <button onClick={() => del(d.id)} className="p-1.5 rounded hover:bg-red-50" data-testid="doc-delete">
                      <Trash2 size={14} className="text-red-700"/>
                    </button>
                  </Tip>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-xs opacity-60" data-testid="docs-total">
        {total} document{total === 1 ? "o" : "i"} totali
      </div>
      <DocumentPreview docId={previewId} open={!!previewId} onClose={() => setPreviewId(null)} />
    </div>
  );
}
